import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FileRow } from '@/components/ged/FileRow'
import { FileDrawer } from '@/components/ged/FileDrawer'
import { getRecents, meKeys } from '@/lib/api/me'

const PAGE_SIZE = 20

export function RecentsPage() {
  const [page, setPage] = useState(1)
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: meKeys.recents(page, PAGE_SIZE),
    queryFn: () => getRecents(page, PAGE_SIZE),
    placeholderData: (prev) => prev,
  })

  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <div style={{ flex: 1, padding: '24px 32px', overflow: 'auto', minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 13,
            color: 'var(--ev-fg-muted)',
            marginBottom: 18,
          }}
        >
          <Link to="/" style={{ color: 'var(--ev-fg-muted)', textDecoration: 'none' }}>
            Início
          </Link>
          <span style={{ color: 'var(--ev-fg-faint)', fontSize: 11 }}>›</span>
          <span style={{ color: 'var(--ev-fg)', fontWeight: 500 }}>Recentes</span>
        </div>

        <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Clock size={20} style={{ color: 'var(--ev-accent)' }} />
          <h1 style={{ fontSize: 24, color: 'var(--ev-fg-strong)', margin: 0 }}>
            Acessados recentemente
          </h1>
        </div>

        {isLoading && (
          <p style={{ color: 'var(--ev-fg-muted)', fontSize: 13 }}>Carregando…</p>
        )}
        {!isLoading && items.length === 0 && (
          <p style={{ color: 'var(--ev-fg-muted)', fontSize: 13 }}>
            Você ainda não acessou nenhum arquivo.
          </p>
        )}

        {items.length > 0 && (
          <>
            <div
              style={{
                background: 'var(--ev-bg-elevated)',
                border: '1px solid var(--ev-border)',
                borderRadius: 10,
                overflow: 'hidden',
                marginBottom: 14,
              }}
            >
              {items.map((d) => (
                <FileRow key={d.id} doc={d} showArea onClick={setSelectedDocId} />
              ))}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 13,
                color: 'var(--ev-fg-muted)',
              }}
            >
              <span>
                Mostrando {Math.min((page - 1) * PAGE_SIZE + 1, totalCount)}–
                {Math.min(page * PAGE_SIZE, totalCount)} de {totalCount}
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ‹
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((n) => (
                  <Button
                    key={n}
                    variant={n === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPage(n)}
                    style={{ minWidth: 30, padding: 0, justifyContent: 'center' }}
                  >
                    {n}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  ›
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <FileDrawer documentId={selectedDocId} onClose={() => setSelectedDocId(null)} />
    </div>
  )
}
